var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { memo, useCallback, useContext, useState } from 'react';
import { unwrap } from 'api/schema/utils';
import Scrollable from 'common/Scrollable';
import { OrderSpecificationContext } from 'views/OrdersList/OrderSpecificationProvider';
import SpecificationFilters from 'views/SpecificationsDashboard/Specifications/SpecificationFilters';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import './OrderModelSpecificationView.scss';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import ADD_ORDER_MODELS from 'api/mutations/ADD_ORDER_MODELS';
import REMOVE_ORDER_MODELS from 'api/mutations/REMOVE_ORDER_MODELS';
import Loader from 'common/Loader';
import { dataValidationErrorHandler } from 'errors/dataValidationError';
import GET_ORDER_SUPPLIERS from 'api/queries/GET_ORDER_SUPPLIERS';
import { OrderModelSpecificationFolderTitle } from './OrderModelSpecificationFolderTitle';
import OrderModelSpecification from './OrderModelSpecification';
import useDebounceOrder from './useDebounceOrder';
const OrderModelSpecificationView = () => {
    const { order: orderData, folder, refetchFolderContentsData, changeOptions, changeSearch, folderId, } = useContext(OrderSpecificationContext);
    const { t } = useTranslation('orders');
    const [addOrderModels] = useMutation(ADD_ORDER_MODELS, {
        refetchQueries: [{ query: GET_ORDER_SUPPLIERS, variables: { id: orderData === null || orderData === void 0 ? void 0 : orderData.id } }],
        awaitRefetchQueries: true,
    });
    const [removeOrderModels] = useMutation(REMOVE_ORDER_MODELS, {
        refetchQueries: [{ query: GET_ORDER_SUPPLIERS, variables: { id: orderData === null || orderData === void 0 ? void 0 : orderData.id } }],
        awaitRefetchQueries: true,
    });
    const [isGloballyBusy, setGloballyBusy] = useState(false);
    const [processingSpecifications, setProcessingSpecification] = useState([]);
    const isSpecificationIncludedInOrder = (specificationId, specOrder) => unwrap(specOrder.models)
        .map((e) => e.modelSpecificationNode.id)
        .includes(specificationId);
    const checkIfAllModelsSelected = useCallback((operation) => {
        const allModelSpecifications = unwrap(folder === null || folder === void 0 ? void 0 : folder.modelSpecifications);
        const selectedSpecificationsLength = (allModelSpecifications === null || allModelSpecifications === void 0 ? void 0 : allModelSpecifications.filter((specifiction) => isSpecificationIncludedInOrder(specifiction.id, orderData)).length) || 0;
        let selectedSpecificationsLengthAfterOperation = null;
        if (operation) {
            selectedSpecificationsLengthAfterOperation =
                operation === 'add' ? selectedSpecificationsLength + 1 : selectedSpecificationsLength - 1;
        }
        if (selectedSpecificationsLengthAfterOperation !== null && selectedSpecificationsLengthAfterOperation !== void 0 ? selectedSpecificationsLengthAfterOperation : selectedSpecificationsLength === (allModelSpecifications === null || allModelSpecifications === void 0 ? void 0 : allModelSpecifications.length)) {
            return true;
        }
        else {
            return false;
        }
    }, [folder === null || folder === void 0 ? void 0 : folder.modelSpecifications, orderData]);
    const handleAddOrderModels = useCallback((modelSpecIds) => __awaiter(void 0, void 0, void 0, function* () {
        yield addOrderModels({
            variables: {
                modelSpecificationIds: modelSpecIds,
                orderId: orderData === null || orderData === void 0 ? void 0 : orderData.id,
            },
        });
        yield refetchFolderContentsData(folderId);
        setGloballyBusy(false);
    }), [addOrderModels, folderId, orderData === null || orderData === void 0 ? void 0 : orderData.id, refetchFolderContentsData]);
    const handleRemoveOrderModels = useCallback((orderModelIds) => __awaiter(void 0, void 0, void 0, function* () {
        yield removeOrderModels({
            variables: {
                orderModelIds,
            },
        });
        yield refetchFolderContentsData(folderId);
        setGloballyBusy(false);
    }), [folderId, refetchFolderContentsData, removeOrderModels]);
    const handleSelectAllToggle = useCallback(() => {
        const allModelSpecifications = unwrap(folder === null || folder === void 0 ? void 0 : folder.modelSpecifications);
        if (!allModelSpecifications)
            return;
        setGloballyBusy(true);
        const idsToProcess = allModelSpecifications.map((specification) => specification.id);
        const isAllModelsSelected = checkIfAllModelsSelected();
        if (isAllModelsSelected) {
            const orderModelIdsToRemove = idsToProcess
                .filter((id) => isSpecificationIncludedInOrder(id, orderData))
                .map((specId) => unwrap(orderData === null || orderData === void 0 ? void 0 : orderData.models).find((e) => e.modelSpecificationNode.id === specId).id);
            handleRemoveOrderModels(orderModelIdsToRemove);
        }
        else {
            const specIdsToAdd = idsToProcess.filter((id) => !isSpecificationIncludedInOrder(id, orderData));
            handleAddOrderModels(specIdsToAdd);
        }
    }, [
        checkIfAllModelsSelected,
        folder === null || folder === void 0 ? void 0 : folder.modelSpecifications,
        handleAddOrderModels,
        handleRemoveOrderModels,
        orderData,
    ]);
    const handleSpecificationChange = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const specIdsToAdd = ((_a = values === null || values === void 0 ? void 0 : values.filter((e) => (e === null || e === void 0 ? void 0 : e.type) === 'add')) === null || _a === void 0 ? void 0 : _a.map((e) => e === null || e === void 0 ? void 0 : e.specificationId)) || [];
        const orderModelsToRemove = ((_b = values === null || values === void 0 ? void 0 : values.filter((e) => (e === null || e === void 0 ? void 0 : e.type) === 'remove')) === null || _b === void 0 ? void 0 : _b.map((e) => e === null || e === void 0 ? void 0 : e.orderModelId)) || [];
        const allModelSpecIds = values === null || values === void 0 ? void 0 : values.map((e) => e.specificationId);
        yield addOrderModels({
            variables: {
                modelSpecificationIds: specIdsToAdd,
                orderId: orderData === null || orderData === void 0 ? void 0 : orderData.id,
            },
        }).catch(() => {
            dataValidationErrorHandler();
        });
        yield removeOrderModels({
            variables: {
                orderModelIds: orderModelsToRemove,
            },
        }).catch(() => {
            dataValidationErrorHandler();
        });
        yield refetchFolderContentsData(folderId);
        setProcessingSpecification((prev) => prev === null || prev === void 0 ? void 0 : prev.filter((e) => !allModelSpecIds.includes(e)));
    }), [addOrderModels, folderId, orderData === null || orderData === void 0 ? void 0 : orderData.id, refetchFolderContentsData, removeOrderModels]);
    const [update] = useDebounceOrder(handleSpecificationChange, 400);
    const onCheckboxChange = useCallback((updateParameter) => {
        update(updateParameter);
    }, [update]);
    const handleCheckboxToggle = useCallback((specificationId) => () => {
        var _a;
        setProcessingSpecification((prev) => [...prev, specificationId]);
        if (isSpecificationIncludedInOrder(specificationId, orderData)) {
            const orderModelId = (_a = unwrap(orderData === null || orderData === void 0 ? void 0 : orderData.models).find((e) => e.modelSpecificationNode.id === specificationId)) === null || _a === void 0 ? void 0 : _a.id;
            onCheckboxChange({
                type: 'remove',
                specificationId,
                orderModelId,
            });
        }
        else {
            onCheckboxChange({
                type: 'add',
                specificationId,
            });
        }
    }, [onCheckboxChange, orderData]);
    const getSpecificationCatalogs = (catalogFolder) => (React.createElement("div", { className: "OrderSpecifications" },
        React.createElement("div", { className: "SpecificationFolderContents" },
            React.createElement("div", { className: "SpecificationFolderContents__subfolders wrapper__list" },
                catalogFolder.parentFolder && (React.createElement(OrderModelSpecificationFolderTitle, { key: "..", folder: catalogFolder.parentFolder, isParentFolder: true, className: "wrapper__specification", refreshFolder: refetchFolderContentsData })),
                unwrap(catalogFolder.folders).map((subfolder) => (React.createElement(OrderModelSpecificationFolderTitle, { key: subfolder.id, folder: subfolder, className: "wrapper__specification", refreshFolder: refetchFolderContentsData })))),
            React.createElement("div", { className: "SpecificationFolderContents__specifications wrapper__list" }, orderData &&
                unwrap(catalogFolder.modelSpecifications).map((specification) => (React.createElement(OrderModelSpecification, { key: specification.id, modelSpecification: specification, order: orderData, handleCheckboxToggle: handleCheckboxToggle(specification.id), checkboxChecked: isSpecificationIncludedInOrder(specification.id, orderData), isParentBusy: processingSpecifications.includes(specification === null || specification === void 0 ? void 0 : specification.id) || isGloballyBusy })))))));
    return (React.createElement("div", { className: "Specifications" },
        React.createElement("div", null,
            React.createElement(SpecificationFilters, { folderId: folderId, onSearch: changeSearch, onChangeOptions: changeOptions }),
            React.createElement(Scrollable, { className: "wrapper__main", padding: 10, scrollbarClassName: "Specifications__scrollbar" }, folder && getSpecificationCatalogs(folder))),
        React.createElement("div", { className: "ViewFooter" },
            React.createElement("div", null,
                React.createElement(FormControlLabel, { control: isGloballyBusy || (processingSpecifications === null || processingSpecifications === void 0 ? void 0 : processingSpecifications.length) > 0 ? (React.createElement(Loader, { size: 28 })) : (React.createElement(Checkbox, { checked: checkIfAllModelsSelected(), onClick: handleSelectAllToggle, disabled: isGloballyBusy ||
                            (processingSpecifications === null || processingSpecifications === void 0 ? void 0 : processingSpecifications.length) > 0 ||
                            (orderData === null || orderData === void 0 ? void 0 : orderData.status) !== 'DRAFT' })), label: t('orders:select_all_specifications') })))));
};
export default memo(OrderModelSpecificationView);
